import { useQuery } from '@tanstack/react-query';
import { Box, Paper, Typography, Stack, Chip, List, ListItem, ListItemText } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { api } from '../api/client';
import { useDashboardKpis } from '../hooks/useDashboardKpis';
import { useDashboardWs } from '../hooks/useDashboardWs';
import type { InsideNowItem } from '../api/types';

interface RecentEvent {
  id: number;
  capturedAt: string;
  eventType?: string;
  plate?: string;
  plateDisplay?: string;
  driver?: string;
  confidence?: number;
  errorCode?: string;
  status: string;
  _local?: boolean;
}

function formatDuration(seconds: number): string {
  if (!seconds || seconds < 0) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function elapsedSince(iso: string): number {
  return Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
}

function Kpi({ label, value, color }: { label: string; value: string | number; color?: string }) {
  return (
    <Paper sx={{ p: 2, textAlign: 'center', height: '100%' }}>
      <Typography variant="h4" sx={{ color, fontWeight: 600 }}>{value}</Typography>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
    </Paper>
  );
}

export function DashboardPage() {
  useDashboardWs();
  const kpis = useDashboardKpis();
  const inside = useQuery({
    queryKey: ['inside-now'],
    queryFn: () => api.get<InsideNowItem[]>('/dashboard/inside-now').then((r) => r.data),
    refetchInterval: 30_000,
  });
  const recent = useQuery({
    queryKey: ['recent-events'],
    queryFn: () => api.get<RecentEvent[]>('/dashboard/recent-events?limit=50').then((r) => r.data),
    refetchInterval: 60_000,
  });

  const k = kpis.data;
  const hourlyData = k?.hourlyEntriesToday?.map((c, i) => ({ hour: `${i}:00`, count: c })) ?? [];
  const accuracyPct = k ? Math.round(Number(k.aiAccuracyToday) * 100) : 0;

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>Dashboard en vivo</Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, md: 3 }}><Kpi label="Adentro ahora" value={k?.insideNow ?? '—'} color="success.main" /></Grid>
        <Grid size={{ xs: 6, md: 3 }}><Kpi label="Entradas hoy" value={k?.entriesToday ?? '—'} /></Grid>
        <Grid size={{ xs: 6, md: 3 }}><Kpi label="Salidas hoy" value={k?.exitsToday ?? '—'} /></Grid>
        <Grid size={{ xs: 6, md: 3 }}><Kpi label="Ausencias hoy" value={k?.absencesToday ?? '—'} color="warning.main" /></Grid>
        <Grid size={{ xs: 6, md: 3 }}><Kpi label="Acierto IA" value={`${accuracyPct}%`} /></Grid>
        <Grid size={{ xs: 6, md: 3 }}><Kpi label="Tiempo promedio adentro" value={formatDuration(k?.avgTimeInsideTodaySec ?? 0)} /></Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2, height: 360 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Actualmente adentro ({inside.data?.length ?? 0})</Typography>
            <Box sx={{ overflow: 'auto', maxHeight: 300 }}>
              <List dense>
                {(inside.data ?? []).map((it) => (
                  <ListItem key={it.visitId} divider>
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip label={it.plateDisplay} size="small" color="primary" sx={{ fontFamily: 'monospace' }} />
                          <Typography variant="body2">{it.driver ?? '(sin chofer)'}</Typography>
                        </Stack>
                      }
                      secondary={`Entró ${formatDuration(elapsedSince(it.entryAt))} atrás`}
                    />
                  </ListItem>
                ))}
                {inside.data?.length === 0 && (
                  <Typography color="text.secondary" sx={{ p: 2 }}>Sin vehículos adentro.</Typography>
                )}
              </List>
            </Box>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2, height: 360 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Eventos recientes</Typography>
            <Box sx={{ overflow: 'auto', maxHeight: 300 }}>
              <List dense>
                {(recent.data ?? []).slice(0, 30).map((e, idx) => (
                  <ListItem key={e.id ?? idx} divider>
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip
                            label={e.eventType ?? 'UNRESOLVED'}
                            size="small"
                            color={e.eventType === 'ENTRY' ? 'success' : e.eventType === 'EXIT' ? 'info' : 'warning'}
                          />
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {e.plateDisplay ?? e.plate ?? '(no leída)'}
                          </Typography>
                          {e.driver && <Typography variant="caption" color="text.secondary">— {e.driver}</Typography>}
                        </Stack>
                      }
                      secondary={new Date(e.capturedAt).toLocaleTimeString()}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Paper sx={{ p: 2, height: 320 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Ingresos por hora (hoy)</Typography>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={hourlyData}>
                <XAxis dataKey="hour" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill="#1565c0" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
